import { type FriendsMetric, LOWER_IS_BETTER } from "./FRIENDS_METRICS";
import type { PeriodStats, StatAxis } from "./FriendsBoard";
import { metricValue } from "./metricValue";
import { SIDE_AXES } from "./SIDE_AXES";

export type DuelWinner = "me" | "them" | "tie" | null;

export type DuelLine<Key extends string> = {
  key: Key;
  me: number | null;
  them: number | null;
  // Нет значения у кого-то из двоих — `null`, очко никому.
  winner: DuelWinner;
};

// Показатели истории матчей в сравнении один на один.
export const DUEL_METRICS = [
  "winRate",
  "kda",
  "soulsPerMinute",
  "deaths",
] as const satisfies readonly FriendsMetric[];

export type DuelMetric = (typeof DUEL_METRICS)[number];

const winner = (me: number | null, them: number | null, lowerIsBetter: boolean): DuelWinner => {
  if (me === null || them === null) {
    return null;
  }
  if (me === them) {
    return "tie";
  }
  return me > them !== lowerIsBetter ? "me" : "them";
};

export const sideScore = (stats: PeriodStats, axis: StatAxis): number | null =>
  stats.sides.find((side) => side.axis === axis)?.score ?? null;

export const duelSides = (me: PeriodStats, them: PeriodStats): DuelLine<StatAxis>[] =>
  SIDE_AXES.map((axis) => {
    const mine = sideScore(me, axis);
    const theirs = sideScore(them, axis);
    return { key: axis, me: mine, them: theirs, winner: winner(mine, theirs, false) };
  });

export const duelNumbers = (me: PeriodStats, them: PeriodStats): DuelLine<DuelMetric>[] =>
  DUEL_METRICS.map((metric) => {
    const mine = metricValue(metric, me);
    const theirs = metricValue(metric, them);
    return {
      key: metric,
      me: mine,
      them: theirs,
      winner: winner(mine, theirs, LOWER_IS_BETTER.has(metric)),
    };
  });

// Счёт дуэли: очки за каждую выигранную строку.
export const duelScore = (lines: readonly DuelLine<string>[]): { me: number; them: number } => ({
  me: lines.filter((line) => line.winner === "me").length,
  them: lines.filter((line) => line.winner === "them").length,
});
